// Schema.org Structured Data Generators

export interface ArticleSchemaData {
    title: string;
    description: string;
    image: string;
    author: {
        name: string;
        url?: string;
    };
    datePublished: string;
    dateModified: string;
    publisher: {
        name: string;
        logo: string;
    };
}

export interface FAQItem {
    question: string;
    answer: string;
}

export interface HowToStep {
    name: string;
    text: string;
    image?: string;
}

/**
 * Generate Article schema (JSON-LD)
 */
export const generateArticleSchema = (data: ArticleSchemaData) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: data.title,
        description: data.description,
        image: data.image,
        author: {
            '@type': 'Person',
            name: data.author.name,
            url: data.author.url,
        },
        publisher: {
            '@type': 'Organization',
            name: data.publisher.name,
            logo: {
                '@type': 'ImageObject',
                url: data.publisher.logo,
            },
        },
        datePublished: data.datePublished,
        dateModified: data.dateModified,
    };
};

/**
 * Generate Blog/CollectionPage schema (JSON-LD) for blog listing pages
 */
export interface BlogListItem {
    title: string;
    url: string;
    image?: string;
    description?: string;
    datePublished?: string;
}

export const generateBlogListSchema = (
    blogName: string,
    blogUrl: string,
    blogDescription: string,
    posts: BlogListItem[]
) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: blogName,
        description: blogDescription,
        url: blogUrl,
        mainEntity: {
            '@type': 'ItemList',
            numberOfItems: posts.length,
            itemListElement: posts.map((post, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: post.url,
                name: post.title,
                description: post.description,
                image: post.image,
            })),
        },
    };
};

/**
 * Generate FAQ schema (JSON-LD)
 */
export const generateFAQSchema = (faqs: FAQItem[]) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
                '@type': 'Answer',
                text: faq.answer,
            },
        })),
    };
};

/**
 * Generate HowTo schema (JSON-LD)
 */
export const generateHowToSchema = (
    name: string,
    description: string,
    steps: HowToStep[],
    totalTime?: string,
    image?: string
) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name,
        description,
        image,
        totalTime,
        step: steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.name,
            text: step.text,
            image: step.image,
        })),
    };
};

/**
 * Generate Breadcrumb schema (JSON-LD)
 */
export const generateBreadcrumbSchema = (items: { name: string; url: string }[]) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: item.name,
            item: item.url,
        })),
    };
};

/**
 * Generate Author schema (JSON-LD)
 */
export const generateAuthorSchema = (author: {
    name: string;
    url?: string;
    image?: string;
    jobTitle?: string;
    description?: string;
    social?: string[];
}) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: author.name,
        url: author.url,
        image: author.image,
        jobTitle: author.jobTitle,
        description: author.description,
        sameAs: author.social,
    };
};

/**
 * Generate Organization schema (JSON-LD)
 */
export const generateOrganizationSchema = (org: {
    name: string;
    url: string;
    logo: string;
    description?: string;
    social?: string[];
}) => {
    return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: org.name,
        url: org.url,
        logo: {
            '@type': 'ImageObject',
            url: org.logo,
        },
        description: org.description,
        sameAs: org.social,
    };
};

/**
 * Convert schema object to JSON-LD script tag
 */
export const schemaToScriptTag = (schema: any): string => {
    return `<script type="application/ld+json">${JSON.stringify(schema, null, 2)}</script>`;
};

/**
 * Extract FAQ items from content
 * Looks for patterns like "Q:" or "Question:" or heading + answer
 */
export const extractFAQFromContent = (html: string): FAQItem[] => {
    const faqs: FAQItem[] = [];

    // Pattern 1: Q: ... A: ...
    const qaPattern = /Q:?\s*(.+?)\s*A:?\s*(.+?)(?=Q:|$)/gis;
    let match;
    while ((match = qaPattern.exec(html)) !== null) {
        faqs.push({
            question: match[1].trim(),
            answer: match[2].trim(),
        });
    }

    return faqs;
};
